import { map } from "nanostores";
import { useCallback } from "react";

import {
  Entity,
  getEntity,
  getEntityByQualifiedName,
  listTopEntities,
  searchEntities,
} from "../api";
import {
  buildResourceAsyncSelectCacheMessages,
  useResourceAsyncSelectCache,
} from "./useResourceAsyncSelectCache";

const $entityCache = map<Record<string, Entity>>({});

const MESSAGES = buildResourceAsyncSelectCacheMessages("entity", {
  loadInitialPrefix: "top ",
});

const fetchEntityById = async (id: string): Promise<Entity | null> => {
  const result = await getEntity({ path: { entityId: id } });
  return result.data ?? null;
};

const searchEntitiesForSelect = async (
  searchTerm: string,
): Promise<Entity[]> => {
  const result = await searchEntities({
    query: { searchTerm },
  });
  return result.data ?? [];
};

const loadDefaultEntities = async (): Promise<Entity[]> => {
  const result = await listTopEntities({
    query: { limit: 10 },
  });
  return result.data ?? [];
};

export const useEntityCache = (): {
  entities: Record<string, Entity>;
  getEntityById: (id: string) => Entity | undefined;
  getEntityByQualifiedName: (qualifiedName: string) => Entity | undefined;
  isLoading: boolean;
  loadEntityByIdentifier: (
    identifier: string,
    type: "id" | "qualifiedName",
  ) => Promise<Entity | null>;
  loadOptions: (inputValue: string) => Promise<Entity[]>;
  searchResults: Entity[];
  selectEntity: (entity: Entity) => void;
} => {
  const {
    getById,
    isLoading,
    loadById,
    loadOptions,
    record,
    runWithLoading,
    searchResults,
    select,
  } = useResourceAsyncSelectCache({
    fetchById: fetchEntityById,
    loadDefaultBatch: loadDefaultEntities,
    messages: MESSAGES,
    search: searchEntitiesForSelect,
    store: $entityCache,
  });

  const getEntityByQualifiedNameFromCache = useCallback(
    (qualifiedName: string): Entity | undefined => {
      return $entityCache.get()[qualifiedName];
    },
    [],
  );

  const loadEntityByIdentifier = useCallback(
    async (
      identifier: string,
      type: "id" | "qualifiedName",
    ): Promise<Entity | null> => {
      if (type === "id") {
        return loadById(identifier);
      }

      const cached = getEntityByQualifiedNameFromCache(identifier);
      if (cached) {
        return cached;
      }

      return runWithLoading(async () => {
        try {
          const result = await getEntityByQualifiedName({
            path: { qualifiedName: identifier },
          });
          if (result.data) {
            select(result.data);
            return result.data;
          }
        } catch (err) {
          console.error(MESSAGES.fetchSelected, err);
        }
        return null;
      });
    },
    [getEntityByQualifiedNameFromCache, loadById, runWithLoading, select],
  );

  return {
    entities: record,
    getEntityById: getById,
    getEntityByQualifiedName: getEntityByQualifiedNameFromCache,
    isLoading,
    loadEntityByIdentifier,
    loadOptions,
    searchResults,
    selectEntity: select,
  };
};
